<template>
  <Form
    ref="observer"
    v-slot="{ handleSubmit }"
  >
    <section v-if="showEmail">
      <b-notification
        :aria-close-label="$t('message.close')"
        type="is-info is-light"
        class="border-notif-info-light"
      >
        <span
          class="column no-break"
          v-html="$t('login.info-mail-validate-key', { email })"
        />
      </b-notification>
      <TextField
        v-model:value="verificationKey"
        rules="length:12"
        label="login.lost-pwd.temporary-pwd"
        name="register.certifieEmail.temporary-pwd"
        place-holder="login.verificationKey-placeholder"
        :mandatory="true"
        @update:value="verificationKey = $event"
      />
    </section>
    <section v-else>
      <TextField
        v-model:value="login"
        rules="min:4"
        label="login.login"
        name="register.login"
        place-holder="login.login-placeholder"
        :mandatory="true"
        :hide-error-message="true"
        @update:value="login = $event"
      />
      <TextField
        v-model:value="password"
        type="password"
        label="login.pwd"
        name="register.password"
        place-holder="login.pwd-placeholder"
        :mandatory="true"
        @update:value="password = $event"
      />
      <TextField
        v-model:value="confirmedPwd"
        type="password"
        rules="confirmed:register.password"
        label="login.confirm-pwd"
        name="register.confirm_password"
        place-holder="login.pwd-placeholder"
        :mandatory="true"
        @update:value="confirmedPwd = $event"
      />
      <TextField
        v-model:value="email"
        type="email"
        rules="email"
        label="login.email"
        name="register.email"
        place-holder="login.email-placeholder"
        :mandatory="true"
        :hide-error-message="true"
        @update:value="email = $event"
      />
    </section>

    <div class="buttons">
      <b-button
        aria-description="login.register"
        :aria-label="$t('login.aria-btn-signup')"
        icon-left="user-plus"
        type="is-primary"
        @click="handleSubmit(register)"
      >
        {{ $t("login.register") }}
      </b-button>
    </div>
    <b-dialog v-if="validatedAccount">
      {{ $t("register.account-active") }}
    </b-dialog>
  </Form>
</template>

<script lang="ts">
import services from "@/composable/services";
import useText from "@/composable/components/text";
import useBoolean from "@/composable/components/boolean";
import { onMounted, inject, ref, Ref } from "vue";
import { Form } from "vee-validate";
import type { VeeForm } from "@/types/vee-validate";
import { useCrossFieldConfirmation } from "@/composable/forms/useCrossFieldConfirmation";
import { useFieldErrorReset } from "@/composable/forms/useFieldErrorReset";
import TextField from "@/components/forms/TextField.vue";
import { useI18n } from "vue-i18n";
import { ErrorType } from "@/composable/application/errors";
import { CreateUserRequest } from "@/types/generated/UsedGeneratedTypes";

export default {
  name: "UserRegister",
  components: { TextField, Form },
  emits: ["user:registered"],
  setup() {
    const login: Ref<string, string> = inject("login") || useText().refText;
    const email: Ref<string, string> = inject("email") || useText().refText;
    const password = useText().refText;
    const confirmedPwd = useText().refText;
    const verificationKey = useText().refText;
    const showEmail = useBoolean().refBoolean;
    const changeTabToSignIn: (() => void) | undefined = inject("changeTabToSignIn");
    const { refBoolean: validatedAccount, doChangeBoolean: changevalidatedAccount } = useBoolean();
    const { t } = useI18n();
    /**
     * Ref vers la balise {@code <Form ref="observer">} de vee-validate .
     * Utilise pour re-trigger la validation cross-field du champ confirm
     * password quand la source change ( vee-validate ne le fait pas de
     * lui-meme : la regle {@code confirmed} ne s'execute que lorsque le
     * champ confirm change , pas la source ) .
     */
    const observer = ref<VeeForm | null>(null);
    useCrossFieldConfirmation(password, observer, "register.confirm_password");
    // Reset auto de l'erreur server-side ( 412 EXISTING_LOGIN ) au retype
    // de l'identifiant : sans ce watcher la bordure rouge reste posee tant
    // que l'utilisateur n'a pas re-soumis , meme s'il a corrige sa saisie .
    useFieldErrorReset(login, observer, "register.login");
    useFieldErrorReset(email, observer, "register.email");
    onMounted(() => {
      if (login) {
        login.value = "test";
      }
    });

    async function register() {
      try {
        if (!showEmail.value) {
          await services.loginService.register(login.value, password.value, email.value);
          services.alertService.toastSuccess(t("alert.registered-user"));
          showEmail.value = true;
        }
        else {
          const createUserRequest = {} as CreateUserRequest;
          createUserRequest.login = login.value;
          createUserRequest.password = password.value;
          createUserRequest.verificationKey = verificationKey.value;
          await services.loginService.modifAcount(createUserRequest);
          resetVariables();
          changevalidatedAccount(true);
          if (changeTabToSignIn) {
            changeTabToSignIn();
          }
        }
      }
      catch (error) {
        const err = error as ErrorType;
        services.alertService.toastServerError(err);
        // Marquer en rouge le champ fautif selon le code metier renvoye par
        // le backend ( bordure + icone danger remplacant le check vert ) .
        // Pas de message inline ( {@code hide-error-message=true} sur les
        // TextField login + email ) : le toast porte deja le libelle metier ,
        // un texte sous le champ ferait doublon visuel .
        // - 412 EXISTING_LOGIN  -> champ Identifiant
        // - 422 EXISTING_EMAIL  -> champ Email
        void err.content?.then((body: unknown) => {
          const code = typeof body === "string"
            ? body
            : (typeof body === "object" && body !== null && "message" in body
              ? String((body as Record<string, unknown>).message)
              : "");
          if (code === "EXISTING_LOGIN") {
            observer.value?.setFieldError("register.login", code);
          } else if (code === "EXISTING_EMAIL") {
            observer.value?.setFieldError("register.email", code);
          }
        });
      }
    }

    function resetVariables() {
      password.value = "";
      confirmedPwd.value = "";
      login.value = "";
      email.value = "";
    }

    return {
      observer,
      login,
      password,
      confirmedPwd,
      email,
      verificationKey,
      register,
      resetVariables,
      showEmail,
      validatedAccount,
      changeTabToSignIn,
    };
  },
};
</script>
